import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { useMainPerformanceSatisfactionComparison, SectionProps } from '../../hooks/useMainPerformanceSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EmptyState from '../ui/EmptyState';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface CustomerSatisfactionComparisonProps extends SectionProps {
  branchId: number;
  timeRange?: string;
}

const CustomerSatisfactionComparison: React.FC<CustomerSatisfactionComparisonProps> = ({
  branchId,
  timeRange = 'last6Months',
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: comparisonData, isLoading, isError, error, refetch } = useMainPerformanceSatisfactionComparison(branchId, timeRange);
  
  // Component metadata
  const componentId = 'customer-satisfaction-comparison';
  const componentTitle = 'مقایسه رضایت مشتریان';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'} animate-enter animate-delay-3`}>
        <div className="card-base card-padding">
          <div className="flex flex-col items-center justify-center h-64">
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری مقایسه رضایت…"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'}`}>
        <div className="card-base card-padding">
          <ErrorMessage 
            message="خطا در دریافت اطلاعات مقایسه رضایت"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  if (!comparisonData?.satisfaction) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'}`}>
        <div className="card-base card-padding">
          <EmptyState />
        </div>
      </div>
    );
  }

  const satisfaction = comparisonData.satisfaction;

  return (
    <div ref={chartRef} className={`${compact ? 'col-span-1' : 'col-span-1'} card-base card-padding animate-enter animate-delay-3`}>
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="mainPerformance"
        onInfo={() => openGuide(CHART_GUIDES.customerSatisfactionComparison)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'mainPerformance',
          componentType: 'chart',
          description: 'مقایسه رضایت مشتریان بین شعبه، برند و کل',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 lg:grid-cols-2 gap-6',
            companions: ['monthly-sales-comparison']
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card">{componentTitle}</h3>
      <div style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار مقایسه رضایت مشتریان بین شعبه فعلی، میانگین برند و میانگین کل">
        <Bar
          data={{
            labels: ['شعبه فعلی', 'میانگین برند'],
            datasets: [
              {
                label: 'درصد رضایت',
                data: [
                  satisfaction.branch != null ? Number(satisfaction.branch.toFixed(1)) : 0,
                  satisfaction.brand != null ? Number(satisfaction.brand.toFixed(1)) : 0,
                ],
                backgroundColor: getChartBackgroundColor(1, currentTheme, 0.8),
                borderColor: getChartColors(currentTheme)[1],
                borderWidth: 2,
                borderRadius: 6,
              }
            ]
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: { display: false },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const label = 'درصد رضایت';
                    const value = `${context.parsed.y}%`;
                    const separator = '\u00A0:\u00A0';
                    return `${label}${separator}${value}`;
                  }
                }
              }
            },
            scales: {
              ...getChartJsOptions(currentTheme).scales,
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                min: 0,
                max: 105,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return `${value}%`;
                  }
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default CustomerSatisfactionComparison;